/**
 * Custom hook untuk mengambil data kalender booking dengan parameter dan opsi yang fleksibel.
 *
 * API Endpoint: GET `/api/booking-calendar`
 * API Version: 3.0
 *
 * @param {DataCalendarParams} params - Object yang berisi parameter untuk filter API.
 * @param {DataCalendarQueryOptions} options - Object untuk mengkonfigurasi atau menimpa opsi default dari `useQuery`.
 * @returns {object} - Return value dari `useQuery`, termasuk `data`, `isLoading`, `error`, dll.
 * @example
 * // Penggunaan dasar
 * const { data } = useDataCalendarV3({
 * filter: {
 * property_id: 'prop-123',
 * date: {
 * gte: '2025-08-01',
 * lte: '2025-08-31',
 * }
 * }
 * });
 *
 * @example
 * // Penggunaan dengan menimpa opsi useQuery (misalnya, menonaktifkan refetch interval)
 * const { data: unassignedData } = useDataCalendarV3({
 * filter: {
 * room_id: 'unassigned',
 * property_id: 'prop-123',
 * date: {
 * gte: '2025-08-01',
 * lte: '2025-09-30',
 * }
 * }
 * },
 * {
 * queryKey: ['unassignedBookings'], // Query key kustom
 * refetchInterval: false, // Menonaktifkan refetch periodik
 * staleTime: 60000 // Data dianggap fresh selama 1 menit
 * });
 */

import { useQuery, UseQueryOptions, QueryKey } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { DataCalendarResponseSchema, DataCalendarResponse } from "../response/DataCalendarResponseV3";

const API_VERSION = 'v3';

/**
 * Tipe untuk parameter filter yang dikirim ke API.
 * Wajib menyertakan `date.gte` dan `date.lte`.
 */
interface DataCalendarParams {
    [key: string]: any;
}

/**
 * Tipe untuk opsi useQuery yang bisa di-override oleh pengguna hook.
 * Menghilangkan `queryKey` dan `queryFn` agar tidak bisa diubah dari luar hook.
 */
type DataCalendarQueryOptions = Omit<UseQueryOptions<DataCalendarResponse, Error, DataCalendarResponse, QueryKey>, 'queryFn'>;


const useDataCalendarV3 = (params: DataCalendarParams, options?: DataCalendarQueryOptions) => {
    return useQuery({
        // Kunci query dibuat dinamis berdasarkan parameter untuk memastikan caching yang unik
        queryKey: ['bookings', 'calendar', 'v3', params],

        queryFn: async (): Promise<DataCalendarResponse> => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/booking-calendar`, { params });

                // Validasi response menggunakan Zod
                const validationResult = DataCalendarResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    // Log error validasi ke konsol untuk debugging
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Calendar data format received from server.");
                }

                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Calendar V3 data`, error);
                throw error; // Melempar kembali error agar ditangani oleh React Query
            }
        },

        // --- Opsi Default ---
        // Hook hanya akan aktif jika parameter tanggal yang esensial tersedia.
        retry: import.meta.env.VITE_MAX_RETRY || 3,
        refetchOnWindowFocus: false,

        // ...options disebar di akhir untuk menimpa (override) opsi default
        ...options,
    });
};

export default useDataCalendarV3;